import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, List, Search } from "lucide-react";

const STATUS = {
  "🟢": ["Live", "bg-[#2E7D4F]/15 text-[#2E7D4F] border-[#2E7D4F]/40"],
  "🟡": ["Partial", "bg-[#C99A1E]/15 text-[#8a6a12] border-[#C99A1E]/40"],
  "🔴": ["Not yet", "bg-[#B23A2E]/15 text-[#B23A2E] border-[#B23A2E]/40"],
  "⚪": ["Planned", "bg-[#1C2530]/10 text-[#1C2530]/60 border-[#1C2530]/20"],
};
const STATUS_RE = /^(🟢|🟡|🔴|⚪)\s*/u;
const HEADING_RE = /^(?:#{1,3}\s+|(\d{1,2})[.)]\s+)(.+)$/;
const BULLET_RE = /^(?:[-•*▪●]|\d{1,2}[.)])\s+/;
const LEGEND_LINE_RE = /^(?:legend|status legend|key)\b/i;

export function parseGuide(content) {
  const lines = (content || "").split(/\r?\n/);
  const preamble = { id: "intro", title: "Overview", blocks: [] };
  const sections = [];
  let cur = preamble;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const h = line.match(HEADING_RE);
    if (h && !STATUS_RE.test(line) && line.length < 90 && !/[.:;,]$/.test(line)) {
      cur = { id: `s-${sections.length + 1}`, num: h[1] || String(sections.length + 1), title: h[2].trim(), blocks: [] };
      sections.push(cur);
      continue;
    }
    if (LEGEND_LINE_RE.test(line) && /🟢|🟡|🔴|⚪/u.test(line)) { cur.blocks.push({ type: "legend" }); continue; }
    if (/^⚠/u.test(line)) { cur.blocks.push({ type: "warn", text: line.replace(/^⚠\s*/u, "") }); continue; }
    const s = line.match(STATUS_RE);
    if (s) { cur.blocks.push({ type: "status", key: s[1], text: line.replace(STATUS_RE, "") }); continue; }
    if (BULLET_RE.test(line)) { cur.blocks.push({ type: "li", text: line.replace(BULLET_RE, "") }); continue; }
    if (line.length < 70 && !/[.!?]$/.test(line) && cur.blocks.length > 0 && sections.length > 0) { cur.blocks.push({ type: "sub", text: line }); continue; }
    cur.blocks.push({ type: "p", text: line });
  }
  const all = preamble.blocks.length ? [preamble, ...sections] : sections;
  return all.length ? all : [{ id: "intro", title: "Guide", blocks: [{ type: "p", text: content || "" }] }];
}

export function StatusChip({ k, testId }) {
  const [label, cls] = STATUS[k] || ["", ""];
  return <span data-testid={testId} className={`inline-flex items-center border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider whitespace-nowrap ${cls}`}>{label}</span>;
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-2 my-2" data-testid="guide-legend">
      {Object.keys(STATUS).map((k) => <StatusChip key={k} k={k} />)}
    </div>
  );
}

function Hi({ text, q }) {
  if (!q) return text;
  const parts = String(text).split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
  return parts.map((p, i) => p.toLowerCase() === q.toLowerCase() ? <mark key={i} className="bg-[#C99A1E]/40 text-inherit px-0.5">{p}</mark> : p);
}

function Block({ b, i, q }) {
  if (b.type === "legend") return <Legend />;
  if (b.type === "warn") return (
    <div className="flex gap-2 border border-[#C99A1E]/50 bg-[#C99A1E]/10 px-3 py-2 text-sm text-[#1C2530] my-2" data-testid="guide-warning">
      <AlertTriangle className="w-4 h-4 text-[#8a6a12] shrink-0 mt-0.5" /><span><Hi text={b.text} q={q} /></span>
    </div>
  );
  if (b.type === "status") return (
    <div className="flex items-start gap-2 py-1 text-sm text-[#1C2530]/85"><StatusChip k={b.key} testId={`guide-status-${i}`} /><span><Hi text={b.text} q={q} /></span></div>
  );
  if (b.type === "li") return <li className="text-sm text-[#1C2530]/85 leading-relaxed ml-4 list-disc"><Hi text={b.text} q={q} /></li>;
  if (b.type === "sub") return <div className="font-medium text-[#1C2530] text-sm mt-3 mb-1"><Hi text={b.text} q={q} /></div>;
  return <p className="text-sm text-[#1C2530]/85 leading-relaxed my-1.5"><Hi text={b.text} q={q} /></p>;
}

function Section({ s, open, onToggle, q }) {
  return (
    <section id={s.id} className="border border-[#DFDAD0] bg-white" data-testid={`guide-section-${s.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#EFECE5]" data-testid={`guide-section-toggle-${s.id}`}>
        {open ? <ChevronDown className="w-4 h-4 text-[#1C2530]/50" /> : <ChevronRight className="w-4 h-4 text-[#1C2530]/50" />}
        {s.num && <span className="font-mono text-[10px] text-[#B23A2E] w-5">{s.num}</span>}
        <span className="font-medium text-[#1C2530]"><Hi text={s.title} q={q} /></span>
        <span className="ml-auto font-mono text-[9px] text-[#1C2530]/40">{s.blocks.length} item{s.blocks.length === 1 ? "" : "s"}</span>
      </button>
      {open && <div className="px-5 pb-4 pt-1 border-t border-[#DFDAD0]">{s.blocks.map((b, i) => <Block key={i} b={b} i={`${s.id}-${i}`} q={q} />)}</div>}
    </section>
  );
}

const matches = (s, q) => !q || s.title.toLowerCase().includes(q) || s.blocks.some((b) => (b.text || "").toLowerCase().includes(q));

export function GuideRenderer({ content, compact = false }) {
  const sections = useMemo(() => parseGuide(content), [content]);
  const [openIds, setOpen] = useState(() => new Set((compact ? sections : sections.slice(0, 2)).map((s) => s.id)));
  const [active, setActive] = useState(sections[0]?.id);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visible = useMemo(() => sections.filter((s) => matches(s, q)), [sections, q]);
  const shown = q ? visible.map((s) => ({ ...s, blocks: s.blocks.filter((b) => !b.text || b.text.toLowerCase().includes(q) || b.type === "legend") })) : visible;
  const toggle = (id) => setOpen((o) => { const n = new Set(o); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const jump = (id) => { setOpen((o) => new Set(o).add(id)); setActive(id); document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const allOpen = openIds.size === sections.length;
  const isOpen = (id) => q ? true : openIds.has(id);

  if (!content) return <div className="text-sm text-[#1C2530]/40 italic py-8 text-center">No guide has been set for this role yet.</div>;
  return (
    <div data-testid="help-guide-content" className={`grid gap-6 ${compact ? "" : "md:grid-cols-[260px_1fr]"}`}>
      {!compact && (
        <nav className="md:sticky md:top-4 self-start" data-testid="guide-toc">
          <div className="relative mb-3">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#1C2530]/40" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search this guide…" data-testid="guide-search-input"
              className="w-full border border-[#DFDAD0] bg-white pl-7 pr-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
          </div>
          {q && <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-2" data-testid="guide-search-count">{visible.length} of {sections.length} sections match</div>}
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2"><List className="w-3 h-3" /> Sections</div>
          <ol className="space-y-0.5">
            {visible.map((s) => (
              <li key={s.id}>
                <button onClick={() => jump(s.id)} data-testid={`guide-toc-${s.id}`}
                  className={`w-full text-left flex items-center gap-2 px-2 py-1.5 text-sm border-l-2 ${active === s.id ? "border-[#B23A2E] text-[#1C2530] bg-[#EFECE5]" : "border-transparent text-[#1C2530]/70 hover:text-[#1C2530]"}`}>
                  {s.num && <span className="font-mono text-[10px] text-[#1C2530]/50 w-4 shrink-0">{s.num}</span>}<span className="leading-snug">{s.title}</span>
                </button>
              </li>
            ))}
          </ol>
          {!q && <button onClick={() => setOpen(allOpen ? new Set() : new Set(sections.map((s) => s.id)))} data-testid="guide-toggle-all"
            className="mt-3 font-mono text-[10px] uppercase tracking-wider text-[#3E8F99] hover:underline">{allOpen ? "Collapse all" : "Expand all"}</button>}
        </nav>
      )}
      <div className="space-y-2 min-w-0">
        {shown.length === 0 && <div className="text-sm text-[#1C2530]/50 italic py-6 text-center" data-testid="guide-search-empty">No sections match “{query}”.</div>}
        {shown.map((s) => <Section key={s.id} s={s} open={isOpen(s.id)} onToggle={() => toggle(s.id)} q={q} />)}
      </div>
    </div>
  );
}
